import { client } from "@/services/apollo.service"
import USERS_LIST_QUERY from "@/graphql/admin_users.gql"
import CREATE_CLIENT_ADMIN_USER_MUTATION from "@/graphql/create_client_admin_user.gql"
import UPDATE_USER_ORGANIZATION_MUTATION from "@/graphql/update_user_organization.gql"
import { emitAlert } from "@/helpers/alert.helper"
import { EVENTS } from "@/constants"
import { Commit } from "vuex"
import { AdminUsersState, AdminUser } from "./admin-users"

export default {
  async fetchUsers(
    { commit, state }: { commit: Commit; state: AdminUsersState }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.query({
        query: USERS_LIST_QUERY,
        variables: {
          userSearch: state.userSearch || null,
          catOrganizationId: state.organizationSearch || null,
        },
      })
      const users: AdminUser[] =
        (response.data && response.data.adminUsers) || []
      commit("setUsers", users)
    } catch (err) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to load users")
      commit("setUsers", [])
    }
    commit("setLoading", false)
  },

  updateUserSearch(
    { commit, dispatch }: { commit: Commit; dispatch: (action: string) => void },
    value: string
  ) {
    commit("setUserSearch", value)
    commit("setPage", 1)
    dispatch("fetchUsers")
  },

  updateOrganizationSearch(
    { commit, dispatch }: { commit: Commit; dispatch: (action: string) => void },
    value: string
  ) {
    commit("setOrganizationSearch", value || "")
    commit("setPage", 1)
    dispatch("fetchUsers")
  },

  setPage({ commit }: { commit: Commit }, page: number) {
    commit("setPage", page)
  },

  setItemsPerPage({ commit }: { commit: Commit }, itemsPerPage: number) {
    commit("setItemsPerPage", itemsPerPage)
    commit("setPage", 1)
  },

  async createClientAdminUser(
    { dispatch }: { dispatch: (action: string) => void },
    payload: {
      firstname: string
      lastname: string
      email: string
      password: string
      catOrganizationId: string
    }
  ) {
    try {
      const response = await client.mutate({
        mutation: CREATE_CLIENT_ADMIN_USER_MUTATION,
        variables: {
          input: payload,
        },
      })
      const result = response.data?.createClientAdminUser
      if (result?.status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, result.message || "User created successfully")
        dispatch("fetchUsers")
        return result
      }
      emitAlert(EVENTS.GLOBAL_WARNING, result?.message || "User creation failed")
      return result
    } catch (err) {
      const message = (err as any)?.graphQLErrors?.[0]?.message || "Failed to create user"
      emitAlert(EVENTS.GLOBAL_ERROR, message)
      throw err
    }
  },

  async updateUserOrganization(
    { dispatch }: { dispatch: (action: string) => void },
    payload: { userId: string; catOrganizationId: string }
  ) {
    try {
      const response = await client.mutate({
        mutation: UPDATE_USER_ORGANIZATION_MUTATION,
        variables: {
          input: payload,
        },
      })
      const result = response.data?.updateUserOrganization
      if (result?.status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, result.message || "Organization updated successfully")
        dispatch("fetchUsers")
        return result
      }
      emitAlert(EVENTS.GLOBAL_WARNING, result?.message || "Update failed")
      return result
    } catch (err) {
      const message = (err as any)?.graphQLErrors?.[0]?.message || "Failed to update organization"
      emitAlert(EVENTS.GLOBAL_ERROR, message)
      throw err
    }
  },
}
